import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../../ui/Button";
import { fetchPipeline, fetchMetrics } from "./api";
import PipelineGraph from "./PipelineGraph";
import TimeScrubber from "./TimeScrubber";
import StageDetailPanel from "./StageDetailPanel";
import ThroughputChart, { RESOLUTION_OPTIONS } from "./ThroughputChart";
import { buildTimeline } from "./timelineUtils";

const HOUR_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 3, label: "3 hours" },
  { value: 6, label: "6 hours" },
  { value: 12, label: "12 hours" },
  { value: 24, label: "24 hours" },
  { value: 48, label: "48 hours" },
];

const FlowLine = () => {
  const [pipeline, setPipeline] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [hours, setHours] = useState(24);
  const [resolutionMinutes, setResolutionMinutes] = useState(5);
  const [selectedStageKey, setSelectedStageKey] = useState(null);
  const [timeIndex, setTimeIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const timeline = useMemo(
    () => buildTimeline(metrics, pipeline),
    [metrics, pipeline]
  );

  const selectedTimestamp = timeline[timeIndex] ?? null;

  useEffect(() => {
    if (timeline.length > 0) {
      setTimeIndex(timeline.length - 1);
    }
  }, [timeline]);

  const loadPipeline = useCallback(async () => {
    const data = await fetchPipeline();
    setPipeline(data);
  }, []);

  const loadMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchMetrics(hours, 60);
      setMetrics(data);
    } catch (err) {
      setError(err.message || "Failed to load queue metrics");
    } finally {
      setIsLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    loadPipeline().catch((err) => {
      setError(err.message || "Failed to load pipeline");
    });
  }, [loadPipeline]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const handleRefresh = () => {
    loadMetrics();
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-800">Flow Line</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ktx-purple"
          >
            {HOUR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            onClick={handleRefresh}
            disabled={isLoading}
            className="bg-ktx-purple hover:bg-purple-700"
          >
            {isLoading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Pipeline</h3>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <PipelineGraph
            pipeline={pipeline}
            metrics={metrics}
            selectedStageKey={selectedStageKey}
            onSelectStage={setSelectedStageKey}
            selectedTimestamp={selectedTimestamp}
          />
          <TimeScrubber
            timeline={timeline}
            timeIndex={timeIndex}
            onTimeIndexChange={setTimeIndex}
            disabled={isLoading || !metrics}
            className={selectedStageKey ? undefined : "rounded-b-lg"}
          />
          {selectedStageKey && (
            <StageDetailPanel
              pipeline={pipeline}
              selectedStageKey={selectedStageKey}
              metrics={metrics}
              selectedTimestamp={selectedTimestamp}
            />
          )}
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h3 className="text-sm font-medium text-gray-700">
            Throughput over time
          </h3>
          <label
            className="flex items-center gap-2 text-sm text-gray-700"
            title="How points are grouped on the chart — does not change the time range above"
          >
            <span className="font-medium">Chart grouping</span>
            <select
              value={resolutionMinutes}
              onChange={(e) => setResolutionMinutes(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ktx-purple"
            >
              {RESOLUTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <ThroughputChart
          pipeline={pipeline}
          metrics={metrics}
          selectedStageKey={selectedStageKey}
          hours={hours}
          resolutionMinutes={resolutionMinutes}
          selectedTimestamp={selectedTimestamp}
        />
      </section>
    </div>
  );
};

export default FlowLine;
